/**
 * ApprovalBadge Component
 *
 * Shows a notification badge with the count of pending promotion approvals.
 * Only visible when:
 * - The user has config:promote capability
 * - There are pending promotions (count > 0)
 *
 * Polls every 60 seconds for updated counts.
 */

import { useEffect, useRef } from "react";
import type { ReactElement } from "react";

import { createApprovalApiClient, usePendingCount } from "../../hooks/use-approval-api";
import { useAuthStore } from "../../store/authStore";

/** Polling interval for pending count: 60 seconds */
const POLL_INTERVAL_MS = 60_000;

/** Get API base URL from environment */
function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  return envUrl ?? "http://localhost:8787";
}

export function ApprovalBadge(): ReactElement | null {
  const { hasCapability, token } = useAuthStore();
  const canPromote = hasCapability("config:promote");

  const clientRef = useRef(
    createApprovalApiClient({
      baseUrl: getApiBaseUrl(),
      getAuthToken: () => useAuthStore.getState().token ?? null,
    })
  );

  const { count, refresh } = usePendingCount(clientRef.current);

  // Poll every 60 seconds
  useEffect(() => {
    if (!canPromote || !token) return;

    const intervalId = setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [canPromote, token, refresh]);

  // Don't render if user lacks capability or count is zero
  if (!canPromote || count === 0) {
    return null;
  }

  return (
    <span
      data-testid="approval-badge"
      className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-error px-1.5 text-xs font-semibold text-white"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}
